
export interface ExpenseReportReportWhereParams {
  tripDateStart?: Date
  tripDateEnd?: Date
  locationIds?: string[]
  tlIds?: number[]
  locationLeaderIds?: number[]
}

export interface ExpenseReportReportData {
  trips: {
    tripId: number
    tripDate: Date
    groupName: string
    tlId: number
    tlName: string
    locName: string
    custId: number

  }[],
  tripIds: string,
  docId: string,
  completedDate: Date,
  locationName: string,
  tripStartDate: Date,
  tripEndDate: Date,
  attendees: number,
  locationLeaders: string,
  totalBudget: number,
  tlComplete: string | null,
  tlCompleteTimestamp: Date | null,
  llReview: string | null,
  llReviewTimestamp: Date | null,
  complete: number,
  budgetDiff: string | number,
  cost: number,
  discountAmount: number,
  additionsAmount: number,
  tlComments: string | null,
  revenue: number,
  margin: number,
  marginPercent: number,
  marginPerPerson: number,
  totalSpent: number,
  adjustments: number,
  expenseReportLink: string

}


export const walletCreditStatuses = ["expired", "available", "used"] as const
export type WalletCreditStatus = typeof walletCreditStatuses[number]
export interface WalletCreditRecord {
  id: number
  tripChargePaymentId: number
  tripId: number
  custId: number
  tripDate: Date
  groupName: string
  originationDate: Date
  expirationDate: Date
  expired: boolean
  creditAmount: number
  amountUsed: number
  amountRemaining: number
  quickNote: string
  locationName: string,
  status: WalletCreditStatus


}

export const walletTransactionDirections = ["To Wallet", "To Trip", "To Other"] as const
export type WalletTransactionDirection = typeof walletTransactionDirections[number]

export interface WalletTransactionRecord {
  creditId: number
  tripId: number
  custId: number
  tripDate: Date
  groupName: string
  locationName: string
  transferDate: Date
  transferAmount: number
  quickNote: string
  direction: WalletTransactionDirection
}

export interface PostTripPayment {
  amountPaid: number
  quickNote: string
  paymentType: PaymentTypes
  paymentCheckNumber: string
  paymentReceivedDate: string

}

export interface PostTripWalletPayment {
  amountPaid: number,
  quickNote: string,
  sendReceipt: boolean,
  incomingWalletId: number
}

export interface PostWalletDonation {
  amountPaid: number,
  quickNote: string,
  sendReceipt: boolean,
  incomingWalletId: number
}

export type PostTripChargeType = Extract<ChargeTypes, ChargeTypes.Additional_Charges | ChargeTypes.Discount | ChargeTypes.Ministry_Money>
export interface PostTripCharge {
  amount: number
  perPerson: boolean
  perPersonCount: number
  description: string
  chargeType: PostTripChargeType,
  promoCode?: string
  chargeDueDate?: Date
}

export interface WalletIncoming {
  id: number,
  tripChargePaymentId: number,
  expirationDate: Date
}

export interface Walletoutgoing {
  id: number,
  tripChargePaymentId: number,
  incomingWalletId: number,
  otherAmount: number,
  note: string,
  timestamp: Date
}

export interface CustomerCredit extends WalletIncoming {
  originationDate: Date,
  tripId: number,
  custId: number,
  note: string,
  transferAmount: number,
  amountUsed: number,
  amountRemaining: number,
  expired: boolean,
  expirationDate: Date
}


export interface WalletTransaction {
  id: number,
  tripId: number,
  custId: number,
  amountPaidDate: Date,
  transferAmount: number,
  note: string,
  type: "payment" | "other"
}

export enum ChargeTypes {
  Registration = 1,
  First_Payment = 2,
  Final_Payment = 3,
  Discount = 5,
  // 4 intentionally left out.  Seems to be a legacy type that isn't actually used
  Additional_Charges = 6,
  Ministry_Money = 7

}

export interface TripChargeTemplate {
  name: string,
  promoCode: string,
  amount: TripChargeTemplateItem<number | null>,
  perPerson: TripChargeTemplateItem<boolean>,
  perPersonCount: TripChargeTemplateItem<number>,
  type: TripChargeTemplateItem<PostTripChargeType>,
  note: TripChargeTemplateItem<string>
}

export interface TripChargeTemplateItem<T> {
  value: T,
  readonly: boolean
}

export interface OnTimeIncentiveAwardedTo {
  tripId: number,
  tripName: string,
  amount: number,
  wouldRefuse: boolean,
}

export interface AlternatePaymentScheduleAmountRule {
  kind: "flat" | "perPerson" | "remainder",
  amount?: number,
}

export interface AlternatePaymentScheduleDueRule {
  kind: "originalDueDate" | "offsetDays",
  days?: number,
}

export interface AlternatePaymentScheduleInstallmentRule {
  amount: AlternatePaymentScheduleAmountRule,
  due: AlternatePaymentScheduleDueRule,
}

export interface AlternatePaymentScheduleOption {
  key: string,
  label: string,
  splitsChargeTypeId: number,
  splitsChargeTypeName: string,
  installments: AlternatePaymentScheduleInstallmentRule[],
}

export interface TripFinanceChargesPayload {
  charges: UiTripCharge[],
  onTimeIncentiveAwardedTo: OnTimeIncentiveAwardedTo | null,
  dueDateLimit: Date,
}

export interface UiTripCharge {
  id: number,
  tripId: number,
  userId: number,
  chargeTypeId: ChargeTypes,
  chargeDueDate: Date,
  chargeAmount: number,
  description: string,
  chargeAmountPaid: number,
  discountAmount: number,
  dateCreated: Date,
  invoice?: number,
  perPersonAmount?: number,
  perPersonCount: number,
  amount: number,
  promoCode: string
  chargeTypeName: string,
  installmentIndex: number,
  chargeOrder: number,
  chargeRemaining: number,
  expectedPaymentDate: Date
}

export type NextPaymentStatus =
  | {
      hasNextPayment: true;
      dueDate: Date;
      amount: number;
      description: string;
      chargeTypeName: string;
      pastDueAmount: number;
    }
  | {
      hasNextPayment: false;
      pastDueAmount: number;
    };


export enum PaymentTypes {
  Check = "Check",
  Stripe = "Stripe",
  PayPal = "PayPal",
  Transfer = "Transfer",
  TransferToWallet = "Wallet",
  Refund = "Refund",
  PpmGlobal = "PPM Global",
  Other = "Other",
  None = "",
  ACH = "ACH"

}
export interface TripChargePayment {
  id: number,
  userId: number,
  tripId: number,
  amountPaid: number,
  amountPaidDate: Date,
  quickNote: string,
  paymentType: PaymentTypes,
  paymentCheckNumber: string,
  paymentReceivedDate: Date
}

export enum AchStatus {
  Succeeded = "succeeded",
  Failed = "failed",
  Pending = "pending"
}

export interface AchTransfers {
  id: number,
  tripId: number,
  objectId: string,
  amount: number,
  status: AchStatus,
  created: Date
}

export interface TripChargeDetail {
  tripChargeId: number,
  chargeTypeId: ChargeTypes,
  installmentIndex: number,
  tripId: number,
  chargeAmount: number,
  chargeDueDate: Date,
  tripYear: number,
  tripMonth: number,
  paidOnTime: number,
  paidLate: number,
  remaining: number,
  alternatePaymentSchedule?: string | null,

}

export interface RegistrationFeePaymentReportRow {
  paymentId: number
  tripId: number
  custId: number
  groupName: string
  location: string
  designation: string
  officeName: string
  tripDate: Date | null
  dateReserved: Date | null
  attendees: number
  dropped: number
  chargeAmount: number
  overflowAmount: number
  paymentAmount: number
  paymentReceivedDate: Date | null
  amountPaidDate: Date | null
  paymentType: string
  paymentCheckNumber: string
  paymentNote: string
  consultantName: string
  recruiterName: string
  tlName: string
}

export interface TripFinanceSummary {
  tripId: number,
  custId: number,
  officeId: number,
  tripDate: Date,
  dateReserved: Date,
  groupName: string,
  attendees: number,
  cost: number,
  lastInvoiced?: Date,
  firstNoticePrepared?: Date,
  secondNoticePrepared?: Date,
  noInvoice?: number,
  registrationDue?: Date,
  registrationAmount: number,
  registrationPaidAmount: number,
  isRegistrationPaid: number,
  firstPaymentDue: Date,
  firstPaymentAmount: number,
  firstPaymentPaidAmount: number,
  isFirstPaymentPaid: number,
  finalPaymentDue: Date,
  totalCharges: number,
  amountRemaining: number,
  quickNote?: string,
  alternatePaymentSchedule?: string | null,

}

export interface TripFinanceStatementDetail {
  id: number,
  tripId: number,
  lastSent: Date,
  disableEmail: number,
  firstNoticePrepared?: Date,
  secondNoticePrepared?: Date

}

export interface PaymentProgressStats {
  pastDue: PaymentStats;
  thisMonth: PaymentStats;
  totalYear: PaymentStats;
}

export interface PaymentStats {
  totalPaid: number;
  totalDue: number;
  paymentDueMonth: string;
  dueDate: Date;
  paidCount: number;
  paymentCount: number;
}

export interface FinanceStats {
  location: string;
  consultant: string;
  tripDate: Date;
  regDate: Date;
  tripId: number;
  groupName: string;
  custId: number;
  groupLeader: string;
  glEmail: string;
  glPhone: string;
  lastCommDate: Date | null;
  quickNote: string;
  balance: number;
  dueDate: Date;
  paymentType: 'Regi Fee' | 'First Payment' | 'Final Payment' | 'Other Payment';
}

export interface ChargeTypeInstallmentSummary {
  remaining: number
  total: number
}

export interface FinanceSummaryData {
  tripId: number
  discountTotal: number
  ministryMoneyTotal: number
  ministryMoneyReceived: number
  additionalChargesTotal: number
  tripAmountRemaining: number
  totalAmountRemaining: number
  lastPaymentDate: Date | null
  regOrDepositAmount: number
  regOrDepositDue: Date | null
  regOrDepositPaid: number
  regOrDepositInstallments: ChargeTypeInstallmentSummary | null
  firstPaymentAmount: number
  firstPaymentAmountWithDiscount: number
  firstPaymentDue: Date | null
  firstPaymentPaid: number
  firstPaymentInstallments: ChargeTypeInstallmentSummary | null
  finalPaymentAmount: number
  finalPaymentAmountWithDiscount: number
  finalPaymentDue: Date | null
  finalPaymentPaid: number
  finalPaymentInstallments: ChargeTypeInstallmentSummary | null
  tripAmount: number
  totalAmount: number
  locationMargin: number
  liability: number
  netLiability: number
  baseTripCost: number
}

type StripeSubscriptionStatus =
    | 'active'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired'
    | 'past_due'
    | 'paused'
    | 'trialing'
    | 'unpaid';

export interface RecurringDonation {
    type: "stripe_subscription";
    stripeSubscriptionId: string;
    stripeCustomer: string;
    billingCycleAnchor: Date;
    subscriptionType: SubscriptionTypes;
    partnerId: string | null;
    status: StripeSubscriptionStatus
    netsuiteProgramId: number | null;
    customerId: number | null;
    amount: number;
    interval: string;
}

export enum SubscriptionTypes {
    ppm365_direct_partner = 1,
    ppm365_donation = 2,
    disaster_donation = 3,
    general_donation = 4
}


export type DonationImportDetails = TripPaymentDetails | DonationDetails

export interface StripeChargeDetailsBase {
    donationDate: Date
    name: string
    email: string
    address1?: string,
    address2?: string
    city?: string,
    stateprov?: string,
    zipcode?: string,
    paymentMethod: string,
    memo: string | null
}

export interface TripPaymentDetails extends StripeChargeDetailsBase {
    donationType: "tripPayment",
}

export interface DonationDetails extends StripeChargeDetailsBase {
    donationType: "donation"
    netsuiteProgramId: number | null
}


export interface CustomerDonationReview {
  id: number;
  stripeLogId?: number | null;
  paypalLogId?: number | null;
  reason: string;
  reviewedBy: number | null;
  stripeChargeDetails: DonationImportDetails | null
}

export interface DonationReviewWithLogs extends CustomerDonationReview {

  amount: number;
  description: string;
  transactionDate: Date;
}